/**
 * Página de Checkout para Productos/Packs
 * 
 * Esta página permite a los usuarios:
 * - Ver resumen del producto a comprar
 * - Seleccionar método de pago (Tarjeta/Bizum o Transferencia)
 * - Procesar el pago
 * - Ver confirmación de compra
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGTMContext } from '../context/GTMContext';
import productService from '../services/productService';
import paymentService from '../services/paymentService';
import purchaseService from '../services/purchaseService';
import promoService from '../services/promoService';
import api from '../services/api';
import { toast } from 'react-toastify';
import { API_URL } from '../config';
import FutbolistaBuscador from '../components/FutbolistaBuscador';
import '../styles/product-checkout.css';

const ProductCheckout = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const gtm = useGTMContext();

  // Estados del componente
  const [product, setProduct] = useState(null);
  const [inscripcionData, setInscripcionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('redsys');
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [purchaseCheck, setPurchaseCheck] = useState({ canPurchase: true, reason: '' });

  // Estados para promoción de referidos
  const [isNewFutbolista, setIsNewFutbolista] = useState(false);
  const [amigoReferido, setAmigoReferido] = useState(null);
  const [promoReferidoActiva, setPromoReferidoActiva] = useState(null);

  // Cargar datos del producto al montar el componente
  useEffect(() => {
    const loadProductData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Verificar si tenemos datos de inscripción desde la navegación
        if (location.state && location.state.type === 'seguro_standalone') {
          // Es una compra standalone del seguro
          const { futbolista_id, futbolista, seguro } = location.state;

          setInscripcionData({
            futbolista_id,
            futbolista,
            seguro,
            precio_total: seguro.precio_prorrateado,
            type: 'seguro_standalone'
          });

          setProduct({
            _id: 'seguro_standalone',
            name: 'Seguro Voluntario de Accidentes',
            description: `Seguro de accidentes deportivos. Cobertura del ${new Date(seguro.fecha_inicio).toLocaleDateString('es-ES')} al ${new Date(seguro.fecha_fin).toLocaleDateString('es-ES')}.`,
            price: seguro.precio_prorrateado,
            type: 'seguro_standalone',
            image: '/img/1x/icon-team.png'
          });

        } else if (location.state) {
          // Es un paquete de jornadas
          const { paquete, futbolista_id, torneo, equipacion, seguro, precio_total, jornadas_seleccionadas } = location.state;

          setInscripcionData({
            paquete,
            futbolista_id,
            torneo,
            equipacion,
            seguro: seguro || { incluir: false, precio: 0 },
            precio_total,
            // Jornadas seleccionadas manualmente por el tutor (null = automático)
            jornadas_seleccionadas: jornadas_seleccionadas || null
          });

          // Crear un objeto "producto" compatible para el resto del componente
          setProduct({
            _id: paquete.id,
            name: paquete.nombre,
            description: paquete.descripcion,
            price: precio_total,
            type: 'paquete_jornadas',
            details: {
              cantidad_jornadas: paquete.cantidad_jornadas,
              sede: paquete.sede,
              torneo: torneo,
              equipacion: equipacion,
              seguro: seguro || { incluir: false, precio: 0 }
            }
          });
        } else {
          // Es un producto tradicional
          const productResponse = await productService.getById(id);
          if (!productResponse.success) {
            throw new Error(productResponse.message || 'Error al cargar el producto');
          }
          setProduct(productResponse.data);
        }

      } catch (error) {
        console.error('Error al cargar datos del producto:', error);
        setError(error.message || 'Error al cargar el producto');
      } finally {
        setLoading(false);
      }
    };

    if (id || (location.state && location.state.type === 'seguro_standalone')) {
      loadProductData();
    }
  }, [id, location.state]);

  // Verificar elegibilidad de compra cuando cambie el usuario o el producto
  useEffect(() => {
    const checkPurchaseEligibility = async () => {
      if (user && product) {
        try {
          // Para seguro standalone, siempre permitir
          if (product.type === 'seguro_standalone') {
            setPurchaseCheck({ canPurchase: true, reason: '' });
          } else if (product.type === 'paquete_jornadas' && inscripcionData) {
            const paquete = inscripcionData.paquete;

            if (!paquete.jornadas_info.fecha_limite_inscripcion) {
              setPurchaseCheck({ canPurchase: false, reason: 'Sin jornadas programadas' });
              return;
            }

            // NOTA: Ya no bloqueamos por fecha límite
            // La fecha límite solo determina desde qué jornada se empieza a participar

            setPurchaseCheck({ canPurchase: true, reason: '' });
          } else {
            // Para productos tradicionales, usar la verificación original
            console.log('🔧 DEBUG: Verificando elegibilidad de compra para usuario:', user.name);
            console.log('🔧 DEBUG: Product ID:', product._id);
            const purchaseResponse = await productService.canUserPurchaseProduct(product._id);
            console.log('🔧 DEBUG: Respuesta de elegibilidad:', purchaseResponse);
            setPurchaseCheck(purchaseResponse.data);
          }
        } catch (purchaseError) {
          console.error('❌ DEBUG: Error al verificar compra:', purchaseError);
          console.error('❌ DEBUG: Error response:', purchaseError.response);
          console.error('❌ DEBUG: Error message:', purchaseError.message);
          setPurchaseCheck({ canPurchase: false, reason: 'Error al verificar elegibilidad' });
        }
      } else if (!user) {
        console.log('🔧 DEBUG: No hay usuario autenticado');
        setPurchaseCheck({ canPurchase: false, reason: 'Debes iniciar sesión para comprar' });
      }
    };

    checkPurchaseEligibility();
  }, [user?.id, product?._id, inscripcionData]); // Agregar inscripcionData como dependencia

  // Verificar si hay promoción de referido activa
  useEffect(() => {
    const checkPromoActiva = async () => {
      try {
        const response = await promoService.getPromoReferidoActiva();
        if (response.success && response.data) {
          setPromoReferidoActiva(response.data);
          console.log('🎁 Promoción de referido activa:', response.data);
        } else {
          setPromoReferidoActiva(null);
          console.log('ℹ️ No hay promoción de referido activa');
        }
      } catch (error) {
        console.error('Error al verificar promoción activa:', error);
        setPromoReferidoActiva(null);
      }
    };

    checkPromoActiva();
  }, []);

  // Verificar si el futbolista es nuevo (para mostrar campo de referido)
  useEffect(() => {
    const checkIfNewFutbolista = async () => {
      console.log('🔍 DEBUG - Verificando si mostrar buscador de amigo:', {
        futbolista_id: inscripcionData?.futbolista_id,
        promoReferidoActiva: promoReferidoActiva
      });

      if (inscripcionData?.futbolista_id && promoReferidoActiva) {
        try {
          const response = await purchaseService.isNewFutbolista(inscripcionData.futbolista_id);
          setIsNewFutbolista(response.data.isNewFutbolista);
          console.log(`👤 Futbolista ${inscripcionData.futbolista_id} es nuevo:`, response.data.isNewFutbolista);
        } catch (error) {
          console.error('Error al verificar si futbolista es nuevo:', error);
          setIsNewFutbolista(false);
        }
      } else {
        setIsNewFutbolista(false);
      }
    };

    checkIfNewFutbolista();
  }, [inscripcionData?.futbolista_id, promoReferidoActiva]);

  // Redirigir si no está autenticado
  useEffect(() => {
    if (!user && !loading) {
      toast.info('Inicia sesión para comprar productos');
      navigate('/login', {
        state: {
          returnTo: `/products/${id}/checkout`,
          productId: id,
          action: 'purchase'
        }
      });
    }
  }, [user, loading, navigate, id]);

  /**
   * Procesar el pago y la compra
   */
  const handleCheckout = async (e) => {
    e.preventDefault();

    if (!purchaseCheck.canPurchase) {
      toast.error(purchaseCheck.reason);
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      let paymentData;
      let response;

      if (product.type === 'seguro_standalone' && inscripcionData) {
        // Es una compra standalone del seguro - usar endpoint dedicado
        const futbolistaNombre = inscripcionData.futbolista?.nombre || `Futbolista ${inscripcionData.futbolista_id}`;

        paymentData = {
          amount: product.price,
          type: 'insurance_purchase',
          description: `Seguro Voluntario de Accidentes - ${futbolistaNombre}`,
          paymentMethod,
          seguroData: {
            futbolista_id: inscripcionData.futbolista_id,
            futbolista_nombre: futbolistaNombre,
            precio_prorrateado: inscripcionData.seguro.precio_prorrateado,
            precio_base: inscripcionData.seguro.precio_base,
            fecha_inicio_cobertura: inscripcionData.seguro.fecha_inicio,
            fecha_fin_cobertura: inscripcionData.seguro.fecha_fin
          }
        };

      } else if (product.type === 'paquete_jornadas' && inscripcionData) {
        // Es un paquete de jornadas - buscar el producto correspondiente en MongoDB

        // Buscar el producto de MongoDB que corresponde a este paquete
        let mongoProduct;
        try {
          const response = await fetch(`${API_URL}/products/by-paquete/${inscripcionData.paquete.id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('prokids_token')}`
            }
          });
          const data = await response.json();
          mongoProduct = data.success ? data.data : null;
  
        } catch (error) {
          console.error('❌ Error buscando producto MongoDB:', error);
        }

        if (!mongoProduct) {
          toast.error('Error: No se encontró el producto correspondiente');
          setIsProcessing(false);
          return;
        }

        // Obtener el nombre del futbolista
        let futbolistaNombre = 'Futbolista';

        if (inscripcionData.futbolista) {
          // Si tenemos el objeto futbolista completo
          futbolistaNombre = inscripcionData.futbolista.nombre ||
                           inscripcionData.futbolista.name ||
                           `${inscripcionData.futbolista.nombre_completo}` ||
                           `${inscripcionData.futbolista.first_name} ${inscripcionData.futbolista.last_name}`.trim() ||
                           `Futbolista ${inscripcionData.futbolista_id}`;
        } else if (inscripcionData.futbolista_id) {
          // Si solo tenemos el ID, obtener el nombre desde PostgreSQL
          try {
            const futbolistaResponse = await api.get(`/postgres/futbolistas/${inscripcionData.futbolista_id}`);
            if (futbolistaResponse.data.success && futbolistaResponse.data.data) {
              futbolistaNombre = futbolistaResponse.data.data.nombre || `Futbolista ${inscripcionData.futbolista_id}`;
            } else {
              futbolistaNombre = `Futbolista ${inscripcionData.futbolista_id}`;
            }
          } catch (error) {
            console.error('❌ Error obteniendo nombre del futbolista:', error);
            futbolistaNombre = `Futbolista ${inscripcionData.futbolista_id}`;
          }
        }

        const descripcionCompleta = `${product.name} - ${futbolistaNombre} - ${inscripcionData.paquete.sede.nombre}`;

        paymentData = {
          amount: product.price,
          type: 'product_purchase',
          relatedTo: mongoProduct._id, // ← USAR ObjectId de MongoDB
          description: descripcionCompleta,
          paymentMethod,
          inscripcionData: {
            paquete_id: inscripcionData.paquete.id,
            futbolista_id: inscripcionData.futbolista_id,
            futbolista_nombre: futbolistaNombre,
            equipacion: inscripcionData.equipacion,
            seguro: inscripcionData.seguro || { incluir: false },
            torneo_id: inscripcionData.torneo.id,
            amigo_referido_id: amigoReferido?.id || null,
            // Jornadas seleccionadas manualmente por el tutor (null = automático)
            jornadas_seleccionadas: inscripcionData.jornadas_seleccionadas || null
          }
        };

      } else {
        // Es un producto tradicional
        paymentData = {
          amount: product.price,
          type: 'product_purchase',
          relatedTo: product._id,
          description: `Compra de ${product.name}`,
          paymentMethod
        };
      }

      if (paymentMethod === 'redsys') {
        try {

          // Intentar iniciar pago con Redsys
          response = await paymentService.initRedsysPayment(paymentData);

          console.log('📥 Respuesta de initRedsysPayment:', response);

          if (response.success && response.data.redsysForm) {
            console.log('✅ Formulario de Redsys recibido:', response.data.redsysForm);
            console.log('🚀 Redirigiendo a Redsys...');

            // Redirigir a la página de pago de Redsys
            paymentService.redirectToRedsys(response.data.redsysForm);
            return;
          } else {
            console.error('❌ No se recibió formulario válido:', response);
            throw new Error('No se recibió el formulario de pago');
          }
        } catch (redsysError) {
          // Mostrar el error real de Redsys para debugging
          console.error('❌ Error real de Redsys:', redsysError);
          console.error('❌ Respuesta completa:', redsysError.response?.data);
          console.error('❌ Status:', redsysError.response?.status);
          console.error('❌ Headers:', redsysError.response?.headers);

          // Mostrar error específico al usuario
          toast.error(`Error de Redsys: ${redsysError.response?.data?.message || redsysError.message}`);

          // NO simular pago - mostrar el error real
          setIsProcessing(false);
          return;
        }

      } else if (paymentMethod === 'transferencia') {
        // Para transferencia, registrar SOLO la transacción pendiente (NO procesar inscripción)
        await procesarTransferenciaPendiente(paymentData);

        setSuccess(true);
        toast.success('Transferencia registrada. Realiza el pago y envía el justificante para confirmar.');

        // Enviar evento GTM de compra (transferencia pendiente)
        gtm.sendPurchaseEvent(
          paymentData.inscripcionData?.transaction_id || `TRANSFER_${Date.now()}`,
          paymentData.amount,
          'EUR',
          [{
            item_id: product._id,
            item_name: product.name,
            category: product.type === 'paquete_jornadas' ? 'Paquete Jornadas' : 'Producto',
            quantity: 1,
            price: paymentData.amount
          }]
        );

        setTimeout(() => {
          navigate('/wallet'); // Redirigir al wallet para ver la transacción pendiente
        }, 5000);
      }

    } catch (error) {
      console.error('Error processing checkout:', error);
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Error al procesar el pago. Por favor, inténtalo de nuevo.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Procesar transferencia pendiente (NO añade saldo)
   */
  const procesarTransferenciaPendiente = async (paymentData) => {
    console.log('🏦 Iniciando procesarTransferenciaPendiente:', {
      productType: product?.type,
      hasInscripcionData: !!inscripcionData,
      paymentData
    });

    if (product.type === 'seguro_standalone' && inscripcionData) {
      // Transferencia pendiente para seguro standalone
      try {
        const futbolistaNombre = inscripcionData.futbolista?.nombre || `Futbolista ${inscripcionData.futbolista_id}`;

        const datosTransferencia = {
          amount: product.price,
          type: 'insurance_purchase',
          description: `Seguro Voluntario de Accidentes - ${futbolistaNombre}`,
          paymentMethod: 'bank_transfer',
          seguroData: {
            futbolista_id: inscripcionData.futbolista_id,
            futbolista_nombre: futbolistaNombre,
            precio_prorrateado: inscripcionData.seguro.precio_prorrateado,
            precio_base: inscripcionData.seguro.precio_base,
            fecha_inicio_cobertura: inscripcionData.seguro.fecha_inicio,
            fecha_fin_cobertura: inscripcionData.seguro.fecha_fin
          }
        };

        const response = await paymentService.createPendingTransaction(datosTransferencia);
        console.log('✅ Transferencia seguro standalone registrada:', response);
        return response;

      } catch (error) {
        console.error('❌ Error al procesar transferencia seguro standalone:', error);
        throw error;
      }

    } else if (product.type === 'paquete_jornadas' && inscripcionData) {
      try {
        // Obtener el producto de MongoDB que corresponde a este paquete
        let mongoProduct;
        try {
          const response = await fetch(`${API_URL}/products/by-paquete/${inscripcionData.paquete.id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('prokids_token')}`
            }
          });
          const data = await response.json();
          mongoProduct = data.success ? data.data : null;
        } catch (error) {
          console.error('❌ Error buscando producto MongoDB:', error);
        }

        if (!mongoProduct) {
          toast.error('Error: No se encontró el producto correspondiente');
          return;
        }

        console.log('🎯 Producto MongoDB encontrado:', {
          mongoProduct_id: mongoProduct._id,
          mongoProduct_name: mongoProduct.name
        });

        console.log('🔍 DEBUG - Estado antes de crear transferencia:', {
          amigoReferido: amigoReferido,
          amigoReferido_id: amigoReferido?.id,
          isNewFutbolista: isNewFutbolista,
          promoReferidoActiva: promoReferidoActiva
        });

        const amigo_id = amigoReferido?.id || null;
        console.log('🔍 DEBUG - amigo_id extraído:', amigo_id);

        const datosTransferencia = {
          paquete_id: inscripcionData.paquete.id,
          product_id: mongoProduct._id,
          futbolista_id: inscripcionData.futbolista_id,
          user_id: user._id,
          equipacion: inscripcionData.equipacion,
          seguro: inscripcionData.seguro || { incluir: false },
          precio_total: product.price,
          metodo_pago: 'bank_transfer',
          transaction_id: `TRANSFER_PENDING_${Date.now()}_${user._id}`,
          pendiente_confirmacion: true,
          amigo_referido_id: amigo_id,
          // Jornadas seleccionadas manualmente por el tutor (null = automático)
          jornadas_seleccionadas: inscripcionData.jornadas_seleccionadas || null
        };

        console.log('📤 Enviando datos de transferencia pendiente:', datosTransferencia);
        console.log('📤 DEBUG - amigo_referido_id en datosTransferencia:', datosTransferencia.amigo_referido_id);

        // Llamar al backend para registrar SOLO la transacción pendiente
        const response = await fetch('/api/inscripciones/procesar-completa', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('prokids_token')}`
          },
          body: JSON.stringify(datosTransferencia)
        });

        console.log('📥 Respuesta del servidor:', response.status, response.statusText);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Error al registrar transferencia');
        }

        const result = await response.json();
        console.log('✅ Transferencia pendiente registrada:', result);

        return result;

      } catch (error) {
        console.error('❌ Error al procesar transferencia pendiente:', error);
        throw error;
      }
    } else {
      throw new Error('Datos de inscripción no válidos para transferencia');
    }
  };

  /**
   * Formatear precio
   */
  const formatPrice = (price, currency = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  // Estados de carga y error
  if (loading) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Cargando información del producto...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="error-message">
            <h2>Error</h2>
            <p>{error}</p>
            <Link to="/products" className="btn btn-primary">
              Volver a Productos
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="error-message">
            <h2>Producto no encontrado</h2>
            <p>El producto que buscas no existe o no está disponible.</p>
            <Link to="/products" className="btn btn-primary">
              Volver a Productos
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header">
          <h1>Completar Compra</h1>
          <p>Estás a punto de comprar un producto</p>
        </div>

        <div className="checkout-content">
          {/* Resumen del producto */}
          <div className="checkout-summary">
            <div className="product-summary-card">
              <h2>Resumen del Producto</h2>
              <div className="product-summary-content">
                <div className="product-summary-image">
                  <img
                    src={product.image || '/img/1x/icon-team.png'}
                    alt={product.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/img/1x/icon-team.png';
                    }}
                  />
                </div>
                <div className="product-summary-details">
                  <h3>{product.name}</h3>
                  {product.code && <p className="product-code">Código: {product.code}</p>}
                  <p className="product-description">{product.description}</p>

                  {product.type === 'paquete_jornadas' && inscripcionData ? (
                    <div className="product-features">
                      <div className="feature-item">
                        <span className="material-icons">sports_soccer</span>
                        <span>{inscripcionData.paquete.cantidad_jornadas} jornadas</span>
                      </div>
                      <div className="feature-item">
                        <span className="material-icons">location_on</span>
                        <span>{inscripcionData.paquete.sede.nombre} - {inscripcionData.paquete.sede.poblacion}</span>
                      </div>
                      <div className="feature-item">
                        <span className="material-icons">event</span>
                        <span>{inscripcionData.torneo.nombre}</span>
                      </div>
                      {inscripcionData.equipacion?.incluir && (
                        <div className="feature-item">
                          <span className="material-icons">sports</span>
                          <span>Equipación completa (Talla: {inscripcionData.equipacion.talla})</span>
                        </div>
                      )}
                      {inscripcionData.seguro?.incluir && (
                        <div className="feature-item">
                          <span className="material-icons">shield</span>
                          <span>🛡️ Seguro Voluntario de Accidentes</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="product-features">
                      <div className="feature-item">
                        <span className="material-icons">sports_soccer</span>
                        <span>{product.sessions || 'N/A'} sesiones</span>
                      </div>
                      <div className="feature-item">
                        <span className="material-icons">schedule</span>
                        <span>{product.duration || 'N/A'} minutos por sesión</span>
                      </div>
                      {product.includesEquipment && (
                        <div className="feature-item">
                          <span className="material-icons">sports</span>
                          <span>Incluye equipamiento</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Resumen de precio */}
            <div className="price-summary-card">
              <h2>Resumen de Precio</h2>
              <div className="price-summary-content">
                {product.type === 'paquete_jornadas' && inscripcionData ? (
                  <>
                    <div className="price-item">
                      <span>Paquete {inscripcionData.paquete.cantidad_jornadas} jornadas</span>
                      <span>{formatPrice(inscripcionData.paquete.precio)}</span>
                    </div>
                    {inscripcionData.equipacion?.incluir && (
                      <div className="price-item">
                        <span>Equipación completa</span>
                        <span>{formatPrice(inscripcionData.equipacion.precio)}</span>
                      </div>
                    )}
                    {inscripcionData.seguro?.incluir && (
                      <div className="price-item">
                        <span>🛡️ Seguro de Accidentes</span>
                        <span>{formatPrice(inscripcionData.seguro.precio)}</span>
                      </div>
                    )}
                    <div className="price-item">
                      <span>Precio por jornada</span>
                      <span>{formatPrice(inscripcionData.paquete.precio / inscripcionData.paquete.cantidad_jornadas)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="price-item">
                      <span>Precio del producto</span>
                      <span>{formatPrice(product.price, product.currency)}</span>
                    </div>
                    {product.sessions && (
                      <div className="price-item">
                        <span>Precio por sesión</span>
                        <span>{formatPrice(product.price / product.sessions, product.currency)}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="price-total">
                  <span>Total</span>
                  <span>{formatPrice(product.price, product.currency)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Formulario de checkout */}
          <div className="checkout-form-container">
            {success ? (
              <div className="checkout-success">
                <div className="success-icon">
                  <span className="material-icons">check_circle</span>
                </div>
                <h2>¡Compra Completada!</h2>
                <p>Tu compra ha sido procesada correctamente.</p>
                <p>Redirigiendo a productos...</p>
              </div>
            ) : (
              <form className="checkout-form" onSubmit={handleCheckout}>
                {error && <div className="alert alert-danger">{error}</div>}
                
                {/* Mensaje de error si no puede comprar */}
                {!purchaseCheck.canPurchase && (
                  <div className="alert alert-warning">
                    <span className="material-icons">warning</span>
                    {purchaseCheck.reason}
                  </div>
                )}

                {/* Campo de referido (solo para futbolistas nuevos y si hay promo activa) */}
                {isNewFutbolista && product?.type === 'paquete_jornadas' && promoReferidoActiva && (
                  <div className="referido-section" style={{
                    marginBottom: '30px',
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    border: '2px solid #4CAF50'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '15px'
                    }}>
                      <span style={{ fontSize: '24px' }}>🎁</span>
                      <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '18px' }}>
                        {promoReferidoActiva.nombre || '¿Vienes recomendado por un amigo?'}
                      </h3>
                    </div>
                    <p style={{
                      fontSize: '14px',
                      color: '#6c757d',
                      marginBottom: '15px',
                      lineHeight: '1.5'
                    }}>
                      {promoReferidoActiva.descripcion || 'Si un amigo te ha recomendado, indícalo aquí y recibirá 1 jornada gratis como agradecimiento. Solo puedes indicar futbolistas que ya hayan participado en la liga.'}
                    </p>
                    <FutbolistaBuscador
                      onSelect={setAmigoReferido}
                      placeholder="Buscar amigo que te recomendó..."
                      label="Amigo:"
                    />
                    {amigoReferido && (
                      <div style={{
                        marginTop: '10px',
                        padding: '10px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #4CAF50'
                      }}>
                        <p style={{
                          margin: 0,
                          fontSize: '14px',
                          color: '#4CAF50',
                          fontWeight: '600'
                        }}>
                          ✅ {amigoReferido.nombre} recibirá 1 jornada gratis
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Selección de método de pago */}
                <div className="payment-method-section">
                  <h3>Método de Pago</h3>
                  <div className="payment-methods">
                    <div
                      className={`payment-method ${paymentMethod === 'redsys' ? 'active' : ''}`}
                      onClick={() => setPaymentMethod('redsys')}
                    >
                      <div className="payment-method-icon">
                        <span className="material-icons">credit_card</span>
                      </div>
                      <div className="payment-method-info">
                        <div className="payment-method-name">Tarjeta de Crédito o Débito / Bizum</div>
                        <div className="payment-method-description">Pago seguro con Redsys</div>
                      </div>
                    </div>

                    <div
                      className={`payment-method ${paymentMethod === 'transferencia' ? 'active' : ''}`}
                      onClick={() => setPaymentMethod('transferencia')}
                    >
                      <div className="payment-method-icon">
                        <span className="material-icons">account_balance</span>
                      </div>
                      <div className="payment-method-info">
                        <div className="payment-method-name">Transferencia Bancaria</div>
                        <div className="payment-method-description">Enviar justificante al email de contacto</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Información adicional según el método de pago */}
                {paymentMethod === 'redsys' && (
                  <div className="payment-info">
                    <p>Serás redirigido a la pasarela de pago segura de Redsys donde podrás pagar con tarjeta de crédito/débito o Bizum.</p>
                  </div>
                )}

                {paymentMethod === 'transferencia' && (
                  <div className="payment-info">
                    <div className="alert alert-warning">
                      <span className="material-icons">account_balance</span>
                      <div>
                        <strong>Datos para transferencia bancaria:</strong>
                        <p><strong>Nº de cuenta:</strong> ES50 0182 0111 4602 0162 6446</p>
                        <p><strong>Concepto:</strong> Nombre del futbolista</p>
                        <p><strong>Beneficiario:</strong> Liga Prokids</p>
                        <p><strong>Importante:</strong> Envía el justificante al 📧 contactoligaprokids@gmail.com</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botones de acción */}
                <div className="checkout-actions">
                  <Link to="/products" className="btn btn-secondary">
                    Cancelar
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isProcessing || !purchaseCheck.canPurchase}
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner"></span>
                        Procesando...
                      </>
                    ) : (
                      <>
                        <span className="material-icons">shopping_cart</span>
                        Completar Compra
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCheckout;
